import type {
  HostingServerPage,
  HostingServerView,
  HostingSiteCatalogPage,
} from '@tosho/contracts';
import type { ClientDirectory, ClientRecord } from './client-directory-api';
import type { ProviderView } from './party-directory-api';
import type { PrototypeIconName } from './PrototypeIcon';

export type SearchSection =
  | 'dashboard'
  | 'clients'
  | 'providers'
  | 'finance'
  | 'servers'
  | 'connections'
  | 'drive'
  | 'freebe'
  | 'bnp';
export interface GlobalSearchResult {
  readonly key: string;
  readonly label: string;
  readonly detail: string;
  readonly section: SearchSection;
  readonly icon: PrototypeIconName;
  readonly view?: 'sites' | 'hosting-providers';
  readonly client?: ClientRecord;
  readonly provider?: ProviderView;
  readonly server?: HostingServerView;
  readonly site?: HostingSiteCatalogPage['items'][number];
}
const pages: readonly (GlobalSearchResult & { readonly keywords: string })[] = [
  {
    key: 'dashboard',
    label: 'Tableau de bord',
    detail: 'Indicateurs et activité',
    section: 'dashboard',
    icon: 'layout-grid',
    keywords: 'accueil résumé indicateurs',
  },
  {
    key: 'clients',
    label: 'Clients',
    detail: 'Annuaire des Clients',
    section: 'clients',
    icon: 'users-round',
    keywords: 'contact entreprise fiche freebe',
  },
  {
    key: 'providers',
    label: 'Prestataires',
    detail: 'Annuaire des prestataires',
    section: 'providers',
    icon: 'briefcase-business',
    keywords: 'fournisseur partenaire',
  },
  {
    key: 'servers',
    label: 'Serveurs reliés',
    detail: 'Registre des serveurs',
    section: 'servers',
    icon: 'database',
    keywords: 'plesk vps hébergement sauvegarde backup',
  },
  {
    key: 'sites',
    label: 'Sites hébergés',
    detail: 'Domaines et vhosts',
    section: 'servers',
    view: 'sites',
    icon: 'network',
    keywords: 'site serveur sous-domaine cms wordpress matomo',
  },
  {
    key: 'hosting-providers',
    label: 'Hébergeurs',
    detail: 'Catalogue des hébergeurs',
    section: 'servers',
    view: 'hosting-providers',
    icon: 'database',
    keywords: 'hébergement serveur fournisseur',
  },
  {
    key: 'finance',
    label: 'Finances & stats',
    detail: 'Indicateurs financiers',
    section: 'finance',
    icon: 'chart-column',
    keywords: 'trésorerie chiffre affaires ca tva recettes dépenses',
  },
  {
    key: 'connections',
    label: 'Connexions / API',
    detail: 'Services reliés à Tosho',
    section: 'connections',
    icon: 'network',
    keywords: 'connecteur intégration réglages api',
  },
  {
    key: 'freebe',
    label: 'Freebe',
    detail: 'Connexion et synchronisation',
    section: 'freebe',
    icon: 'refresh-cw',
    keywords: 'import synchronisation',
  },
  {
    key: 'drive',
    label: 'Google Drive',
    detail: 'Connexion et stockage',
    section: 'drive',
    icon: 'cloud-upload',
    keywords: 'fichiers documents sauvegardes stockage',
  },
  {
    key: 'bnp',
    label: 'BNP',
    detail: 'Connexion bancaire',
    section: 'bnp',
    icon: 'wallet',
    keywords: 'banque relevés comptes',
  },
];
export function normalizedSearch(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('fr-FR').trim();
}
export function searchPages(query: string, administrator: boolean): readonly GlobalSearchResult[] {
  const terms = normalizedSearch(query).split(/\s+/u).filter(Boolean);
  return pages
    .filter(
      (page) =>
        (administrator || page.section === 'dashboard') &&
        terms.every((term) => normalizedSearch(page.label + ' ' + page.keywords).includes(term)),
    )
    .slice(0, 6);
}
async function read<T>(url: string, signal: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    cache: 'no-store',
    signal: AbortSignal.any([signal, AbortSignal.timeout(10000)]),
  });
  if (!response.ok)
    throw new Error(
      response.status === 401 || response.status === 403
        ? 'SEARCH_ACCESS_UNAVAILABLE'
        : 'SEARCH_UNAVAILABLE',
    );
  return response.json() as Promise<T>;
}
/** Les API métier autorisent chaque lecture depuis la session. Aucun tenant,
 * rôle ou filtre de visibilité fourni par la recherche n'est une preuve de droit.
 * Aucune requête fournisseur Plesk/Freebe/Google n'est déclenchée ici. */
export async function searchBusiness(
  query: string,
  signal: AbortSignal,
): Promise<{ items: readonly GlobalSearchResult[]; incomplete: boolean }> {
  // Les annuaires Client/Prestataire ont une borne commune de 120 caractères.
  const q = encodeURIComponent(query.trim().slice(0, 120));
  const requests = await Promise.allSettled([
    read<ClientDirectory>('/api/clients?q=' + q + '&page=0&mode=clients', signal),
    read<HostingServerPage>('/api/hosting-servers?page=0&pageSize=5&q=' + q, signal),
    read<HostingSiteCatalogPage>(
      '/api/hosting-sites?search=' + q + '&client=ALL&owner=ALL&presence=CURRENT&page=0&pageSize=5',
      signal,
    ),
    read<{ providers: readonly ProviderView[] }>(
      '/api/directory/providers?q=' + q + '&page=0&mode=providers',
      signal,
    ),
  ]);
  signal.throwIfAborted();
  // Une révocation confirmée par une source retire tous les résultats métier
  // de cette recherche ; aucune ancienne suggestion ne reste sélectionnable.
  if (
    requests.some(
      (r) =>
        r.status === 'rejected' &&
        r.reason instanceof Error &&
        r.reason.message === 'SEARCH_ACCESS_UNAVAILABLE',
    )
  )
    throw new Error('SEARCH_ACCESS_UNAVAILABLE');
  const items: GlobalSearchResult[] = [];
  const [clients, servers, sites, providers] = requests;
  if (clients?.status === 'fulfilled')
    for (const client of clients.value.clients
      .filter((c) => c.stage === 'CLIENT' && c.origin !== 'FREEBE_LEGACY_UNREVIEWED')
      .slice(0, 4))
      items.push({
        key: 'client:' + client.id,
        label: client.displayName,
        detail: 'Client',
        section: 'clients',
        icon: 'users-round',
        client,
      });
  if (servers?.status === 'fulfilled')
    for (const server of servers.value.items.slice(0, 4))
      items.push({
        key: 'server:' + server.id,
        label: server.label,
        detail: 'Serveur',
        section: 'servers',
        icon: 'database',
        server,
      });
  if (sites?.status === 'fulfilled')
    for (const site of sites.value.items.slice(0, 4))
      items.push({
        key: 'site:' + site.id,
        label: site.asciiName,
        detail: 'Site · ' + site.serverLabel,
        section: 'servers',
        view: 'sites',
        icon: 'network',
        site,
      });
  if (providers?.status === 'fulfilled')
    for (const provider of providers.value.providers.slice(0, 4))
      items.push({
        key: 'provider:' + provider.id,
        label: provider.displayName,
        detail: 'Prestataire',
        section: 'providers',
        icon: 'briefcase-business',
        provider,
      });
  return { items, incomplete: requests.some((r) => r.status === 'rejected') };
}
